import React, { useState } from "react";
import { useRouter, usePathname } from 'next/navigation';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useContactUsData } from "@/contexts/HomeDataContext";
import { useTranslations } from 'next-intl';

export const ContactUsSection: React.FC = () => {
  const data = useContactUsData();
  const t = useTranslations('ContactUs');
  const tContact = useTranslations('Contact');
  const router = useRouter();
  const pathname = usePathname();
  
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Set submitting state
    setIsSubmitting(true);
    
    // analytics event
    (window as any).dataLayer.push({
      event: "contact_short_form_click",
      source: window.location.href
    });

    // Extract locale from pathname (e.g., /en or /ar)
    const locale = pathname.split('/')[1] || 'en';
    
    // Combine firstName and lastName into full name
    const fullName = `${formData.firstName} ${formData.lastName}`.trim();
    
    // Create query params with the form data
    const params = new URLSearchParams();
    if (fullName) params.append('name', fullName);
    if (formData.email) params.append('email', formData.email);
    if (formData.phone) params.append('phone', formData.phone);
    params.append('prefilled', 'true'); // Flag to show completion message
    
    // Redirect to contact page with pre-filled data (or empty if no data)
    router.push(`/${locale}/contact?${params.toString()}`);
    
    // Reset submitting state after a short delay to allow for visual feedback
    setTimeout(() => setIsSubmitting(false), 500);
  };

  return (
    <section className="relative w-full py-16 md:py-20 lg:py-24 bg-[#F9F9F9]">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-16">
          
          {/* Left Content */}
          <div className="flex-1 max-w-[634px] text-center lg:text-left rtl:lg:text-right">
            <div className="translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:200ms]">
              <div className="bg-brand-gradient-text font-h1-up font-[number:var(--h1-up-font-weight)] text-[length:var(--h1-up-font-size)] tracking-[var(--h1-up-letter-spacing)] leading-[var(--h1-up-line-height)] [font-style:var(--h1-up-font-style)] mb-3 md:mb-4">
                {t('tagline')}
              </div>
              
              <h2 className="font-heading-h1 font-[number:var(--heading-h1-font-weight)] text-x-900 text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-[length:var(--heading-h1-font-size)] tracking-[var(--heading-h1-letter-spacing)] leading-tight md:leading-tight lg:leading-[var(--heading-h1-line-height)] [font-style:var(--heading-h1-font-style)]">
                {t('heading')}
              </h2>
            </div>
          </div>

          {/* Right Content - Form */}
          <div className="flex-1 max-w-[638px] w-full">
            <div className="translate-y-[-1rem] animate-fade-in opacity-0 [--animation-delay:400ms]">
              <form onSubmit={handleSubmit} className="space-y-5">
                
                {/* First Name and Last Name Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Input
                    name="firstName"
                    placeholder={t('form.firstName')}
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="h-[60px] bg-white rounded-[5px] border-0 font-body-m font-[number:var(--body-m-font-weight)] text-x-800 text-[length:var(--body-m-font-size)] tracking-[var(--body-m-letter-spacing)] leading-[var(--body-m-line-height)] [font-style:var(--body-m-font-style)] px-5 shadow-sm focus:shadow-md transition-shadow rtl:text-right"
                  />
                  <Input
                    name="lastName"
                    placeholder={t('form.lastName')}
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="h-[60px] bg-white rounded-[5px] border-0 font-body-m font-[number:var(--body-m-font-weight)] text-x-800 text-[length:var(--body-m-font-size)] tracking-[var(--body-m-letter-spacing)] leading-[var(--body-m-line-height)] [font-style:var(--body-m-font-style)] px-5 shadow-sm focus:shadow-md transition-shadow rtl:text-right"
                  />
                </div>

                {/* Email Row */}
                <div>
                  <Input
                    name="email"
                    type="email"
                    placeholder={t('form.email')}
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full h-[60px] bg-white rounded-[5px] border-0 font-body-m font-[number:var(--body-m-font-weight)] text-x-800 text-[length:var(--body-m-font-size)] tracking-[var(--body-m-letter-spacing)] leading-[var(--body-m-line-height)] [font-style:var(--body-m-font-style)] px-5 shadow-sm focus:shadow-md transition-shadow rtl:text-right"
                  />
                </div>

                {/* Phone and Button Row */}
                <div className="flex flex-col sm:flex-row gap-5">
                  <Input
                    name="phone"
                    type="tel"
                    placeholder={t('form.phone')}
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full sm:flex-1 h-[60px] bg-white rounded-[5px] border-0 font-body-m font-[number:var(--body-m-font-weight)] text-x-800 text-[length:var(--body-m-font-size)] tracking-[var(--body-m-letter-spacing)] leading-[var(--body-m-line-height)] [font-style:var(--body-m-font-style)] px-5 shadow-sm focus:shadow-md transition-shadow rtl:text-right"
                  />
                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full sm:flex-1 h-[60px] px-8 sm:px-10 py-5 rounded-[5px] bg-brand-gradient font-button font-[number:var(--button-font-weight)] text-white text-[length:var(--button-font-size)] tracking-[var(--button-letter-spacing)] leading-[var(--button-line-height)] [font-style:var(--button-font-style)] transition-all duration-200 hover:scale-[1.01] hover:shadow-[0_22px_40px_rgba(53,30,224,0.45)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-secondary focus-visible:ring-offset-2 shadow-[0_16px_32px_rgba(53,30,224,0.35)] disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg 
                          className="animate-spin h-5 w-5" 
                          xmlns="http://www.w3.org/2000/svg" 
                          fill="none" 
                          viewBox="0 0 24 24"
                        >
                          <circle 
                            className="opacity-25" 
                            cx="12" 
                            cy="12" 
                            r="10" 
                            stroke="currentColor" 
                            strokeWidth="4"
                          />
                          <path 
                            className="opacity-75" 
                            fill="currentColor" 
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        {tContact('questionSection.sending')}
                      </span>
                    ) : (
                      t('form.submitButton')
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Background Elements */}
      <div className="absolute top-0 right-0 w-96 h-64 opacity-10 pointer-events-none">
        <svg
          viewBox="0 0 400 300"
          className="w-full h-full"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient id="decorativeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#351EE0" />
              <stop offset="100%" stopColor="#3F74EF" />
            </linearGradient>
          </defs>
          <circle cx="50" cy="50" r="3" fill="url(#decorativeGradient)" />
          <circle cx="150" cy="80" r="2" fill="url(#decorativeGradient)" />
          <circle cx="250" cy="60" r="4" fill="url(#decorativeGradient)" />
          <circle cx="350" cy="100" r="2" fill="url(#decorativeGradient)" />
          <line x1="50" y1="50" x2="150" y2="80" stroke="url(#decorativeGradient)" strokeWidth="1" />
          <line x1="150" y1="80" x2="250" y2="60" stroke="url(#decorativeGradient)" strokeWidth="1" />
          <line x1="250" y1="60" x2="350" y2="100" stroke="url(#decorativeGradient)" strokeWidth="1" />
          <circle cx="100" cy="150" r="2" fill="url(#decorativeGradient)" />
          <circle cx="200" cy="180" r="3" fill="url(#decorativeGradient)" />
          <circle cx="300" cy="160" r="2" fill="url(#decorativeGradient)" />
          <line x1="100" y1="150" x2="200" y2="180" stroke="url(#decorativeGradient)" strokeWidth="1" />
          <line x1="200" y1="180" x2="300" y2="160" stroke="url(#decorativeGradient)" strokeWidth="1" />
        </svg>
      </div>
    </section>
  );
};
